<template>
    <validation-observer ref="form" v-slot="{ handleSubmit, meta }">
        <modal
            :id="modalId"
            title="Settings"
            ok-title="Save"
            size="lg"
            :ok-disabled="!meta.valid || loading"
            @ok.prevent="handleSubmit(_, save)"
            @hidden="hidden"
            @shown="setData"
        >
            <busy-modal :show="loading"></busy-modal>
            <b-form @submit.prevent="handleSubmit(_, save)" v-if="values">
                <page :tabs="tabs" class="settings-page" :compact-mobile-view="true">
                    <b-container fluid class="mt-4">
                        <template v-if="selectedTab === generalTab">
                            <b-row v-if="settings && settings.revenue_recognition_visible">
                                <b-col>
                                    <h6 class="mb-1">Revenue Recognition</h6>
                                    <p class="description">Choose how revenue is recognized on your owner statements.</p>
                                    <b-form-radio
                                        class="mb-2"
                                        v-model="values.revenue_recognition"
                                        name="revenue-recognition"
                                        :value="0"
                                    >
                                        <div>At Check-out</div>
                                        <div class="text-secondary">
                                            Revenue is only recognized at check-out. Owner statements will not reflect any reservations that have not yet checked out.
                                        </div>
                                    </b-form-radio>
                                    <b-form-radio
                                        class="mb-2"
                                        v-model="values.revenue_recognition"
                                        name="revenue-recognition"
                                        :value="1"
                                    >
                                        <div>Pro-rata (recommended)</div>
                                        <div class="text-secondary">
                                            Revenue is recognized each night.
                                            Owner statements will prorate any reservations which fall within the statement period and are still ongoing.
                                            The benefit to this method is that owners won't have to wait till guest checkout to receive partial payment on any ongoing reservations.
                                        </div>
                                    </b-form-radio>
                                </b-col>
                            </b-row>

                            <b-row class="mt-2">
                                <b-col>
                                    <h6 class="mb-1">Tax Calculation</h6>
                                    <p class="description">Choose how taxes are calculated on your owner statements.</p>
                                    <validation-provider
                                        name="tax_calculation"
                                        rules="required"
                                        v-slot="{ errors, handleChange }"
                                    >
                                        <form-select
                                            v-model="values.tax_calculation"
                                            :options="taxCalculationOptions"
                                            :error="errors[0]"
                                            @input="handleChange"
                                        />
                                    </validation-provider>
                                </b-col>
                            </b-row>
                        </template>

                        <template v-if="selectedTab === displayTab">
                            <b-row>
                                <b-col>
                                    <h6 class="mb-1">Reservations Table Columns</h6>
                                    <p class="description">
                                        The columns you select below will appear on both the screen and the PDF versions of the
                                        owner statements.
                                    </p>

                                    <div v-for="column in preTax" :key="column.name" class="mb-2">
                                        <form-switch v-model="column.visible" />
                                        <span class="ml-2">{{ column.name }}</span>
                                    </div>

                                    <div v-if="tax" class="mb-3">
                                        <form-switch v-model="tax.visible" class="mb-2" />
                                        <span class="ml-2">{{ tax.name }}</span>
                                        <span class="description ml-1">
                                            (Will show all taxes set up for each property)
                                        </span>
                                    </div>

                                    <h6 class="mt-3 mb-1">Management Fees</h6>
                                    <p class="description">
                                        These are fees assessed to the owner (not the guest), configured in the property
                                        management module.
                                    </p>
                                    <div v-if="managementFees.length" class="mb-4">
                                        <div v-for="feeColumn in managementFees" :key="feeColumn.name" class="mb-2 ml-3">
                                            <form-switch v-model="feeColumn.visible" />
                                            <span class="ml-2">{{ feeColumn.name }}</span>
                                        </div>
                                    </div>

                                    <div v-for="column in postTax" :key="column.name" class="mb-2">
                                        <form-switch v-model="column.visible" />
                                        <span class="ml-2">{{ column.name }}</span>
                                    </div>
                                </b-col>
                            </b-row>
                        </template>

                        <template v-if="selectedTab === pdfTab">
                            <b-row>
                                <b-col>
                                    <h6 class="mb-1">PDF Font Size</h6>
                                    <p class="description">
                                        Choose a font size for the PDF statements.
                                        <strong>Auto</strong> lets the system determine the size automatically.
                                    </p>
                                    <validation-provider name="pdf_scale" rules="required" v-slot="{ errors, handleChange }">
                                        <form-select
                                            v-model="values.pdf_scale"
                                            :options="scaleOptions"
                                            :error="errors[0]"
                                            @input="handleChange"
                                        />
                                    </validation-provider>
                                </b-col>
                            </b-row>

                            <b-row class="mt-2">
                                <b-col>
                                    <h6 class="mb-1">PDF Orientation</h6>
                                    <p class="description">
                                        Choose <strong>Portrait</strong> or <strong>Landscape</strong> for your PDF layout.
                                    </p>
                                    <validation-provider
                                        name="pdf_orientation"
                                        rules="required"
                                        v-slot="{ errors, handleChange }"
                                    >
                                        <form-select
                                            v-model="values.pdf_orientation"
                                            :options="pdfOrientationOptions"
                                            :error="errors[0]"
                                            @input="handleChange"
                                        />
                                    </validation-provider>
                                </b-col>
                            </b-row>

                            <b-row class="mt-2">
                                <b-col>
                                    <h6 class="mb-1">Additional Text in Statement Header</h6>
                                    <p class="description">
                                        This text will be displayed below the period in the statement PDF header.
                                    </p>
                                    <validation-provider
                                        name="newComments"
                                        v-slot="{ errors, handleChange }"
                                        rules="maxMessageLenOrEqual:250,Additional text"
                                        class="d-flex flex-column"
                                    >
                                        <form-textarea
                                            :rows="4"
                                            v-model="values.business_text"
                                            :error="errors[0]"
                                            @input="handleChange"
                                        />
                                    </validation-provider>
                                </b-col>
                            </b-row>
                        </template>
                    </b-container>
                </page>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import API from '../../api';
import { OWNER_STATEMENT_SETTINGS_ENDPOINT } from '../../endpoints';
import { formatResponseError } from '../../utils/api';
import Page from '../generic/page/page.vue';
import Modal from '../generic/modal/modal.vue';
import BusyModal from '../generic/busy-modal/busy-modal.vue';
import FormSelect from '../generic/form-select/form-select.vue';
import FormSwitch from '../generic/form-switch/form-switch.vue';
import FormTextarea from '../generic/form-textarea/form-textarea.vue';

export default {
    name: 'OwnerStatementSettingsModalForm',
    components: {
        Page,
        Modal,
        BusyModal,
        FormSelect,
        FormSwitch,
        FormTextarea,
    },
    props: {
        modalId: String,
        settings: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            loading: false,
            values: null,
            generalTab: 'General',
            displayTab: 'Display',
            pdfTab: 'PDF',
            selectedTab: 'General',
            pdfOrientationOptions: [
                { value: 'portrait', text: 'Portrait' },
                { value: 'landscape', text: 'Landscape' },
            ],
            taxCalculationOptions: [
                { value: 'ALL_TAX', text: 'All Tax Collected' },
                { value: 'ONLY_TAX_ON_RENTAL_CHARGES', text: 'Only Tax on Rental Charges' },
            ],
        };
    },
    computed: {
        taxIndex() {
            return this.values?.reservations_table_columns.findIndex((c) => c.name === 'Tax');
        },
        preTax() {
            return this.values?.reservations_table_columns.filter(
                (col, index) => index < this.taxIndex && col.category !== 'Management Fees'
            );
        },
        tax() {
            return this.values?.reservations_table_columns.find((col) => col.name === 'Tax');
        },
        managementFees() {
            return this.values?.reservations_table_columns.filter((col) => col.category === 'management_fees');
        },
        postTax() {
            return this.values?.reservations_table_columns.filter(
                (col, index) => index > this.taxIndex && col.category !== 'management_fees'
            );
        },
        scaleOptions() {
            let scaleOptions = [];
            for (var s = 0.3; s < 1.6; s += 0.1) {
                scaleOptions.push({
                    value: s === 0.3 ? 0.0 : s.toFixed(1) * 1,
                    text: s === 0.3 ? 'Auto' : Math.round(s * 100) + '%',
                });
            }
            return scaleOptions;
        },
        requestData() {
            return {
                ...this.values,
                reservations_table_columns: this.values.reservations_table_columns.map((i) =>
                    i.visible ? 0 : -1
                ),
            };
        },
        tabs() {
            return [
                {
                    title: this.generalTab,
                    active: this.selectedTab === this.generalTab,
                    callback: (tabTitle) => this.tabClick(tabTitle),
                },
                {
                    title: this.displayTab,
                    active: this.selectedTab === this.displayTab,
                    callback: (tabTitle) => this.tabClick(tabTitle),
                },
                {
                    title: this.pdfTab,
                    active: this.selectedTab === this.pdfTab,
                    callback: (tabTitle) => this.tabClick(tabTitle),
                }
            ];
        }
    },
    methods: {
        tabClick(selectedTabTitle) {
            this.selectedTab = selectedTabTitle;
            if (selectedTabTitle === this.generalTab || selectedTabTitle === this.pdfTab) {
                this.$refs.form.setValues({
                    pdf_scale: this.values.pdf_scale,
                    pdf_orientation: this.values.pdf_orientation,
                    tax_calculation: this.values.tax_calculation,
                    revenue_recognition: this.values.revenue_recognition,
                });
            }
        },
        getInitialValues() {
            const settings = cloneDeep(this.settings);
            return {
                pdf_scale: settings?.pdf_scale || 0.0,
                pdf_orientation: settings?.pdf_orientation || 'portrait',
                tax_calculation: settings?.tax_calculation || 'ONLY_TAX_ON_RENTAL_CHARGES',
                revenue_recognition: settings?.revenue_recognition || 0,
                reservations_table_columns: settings?.reservations_table_columns || [],
                business_text: settings?.business_text || '',
            };
        },
        hideModal() {
            this.$bvModal.hide(this.modalId);
        },
        hidden() {
            this.values = null;
        },
        async save() {
            try {
                this.loading = true;
                const { data } = await API.patch(OWNER_STATEMENT_SETTINGS_ENDPOINT(), this.requestData);
                this.successToast({
                    text: 'Settings were successfully saved.',
                });
                this.$emit('updateSettings', data);
                this.$nextTick(() => {
                    this.hideModal();
                });
            } catch (error) {
                const { generic, fields } = formatResponseError(error);
                if (generic) {
                    this.errorToast({ text: generic });
                }
                if (!isEmpty(fields)) {
                    this.$refs.form.setErrors(fields);
                }
            } finally {
                this.loading = false;
            }
        },
        setData() {
            this.values = this.getInitialValues();
            this.selectedTab = this.generalTab;
            this.$nextTick(() => {
                this.$refs.form.setValues({
                    pdf_scale: this.values.pdf_scale,
                    pdf_orientation: this.values.pdf_orientation,
                    tax_calculation: this.values.tax_calculation,
                    revenue_recognition: this.values.revenue_recognition,
                });
            });
        }
    },
};
</script>

<style lang="scss" scoped>
    .description {
        font-size: 0.875rem;
        color: #6c757d;
        margin-bottom: 0.5rem;
    }
    
    .settings-page {
        width: 100%;
    }
</style>
